import type React from 'react';

/** A header band spanning adjacent columns — see TableColumn.group. */
export interface TableColumnGroup {
	id: string;
	label: React.ReactNode;
}

/** One cell of the column-group header row: a run of adjacent columns. */
export interface ColumnGroupSpanModel<TColumn> {
	key: string;
	group: TableColumnGroup | null;
	columns: Array<TColumn>;
}

/**
 * Collapses adjacent columns that share a group id into one span. Ungrouped
 * columns each get a span of their own, so a sticky ungrouped column keeps
 * its own (sticky) cell in the group row. A group id that reappears after a
 * different column starts a new span rather than reaching back, because a
 * header cell can only span adjacent columns.
 */
export function buildColumnGroupSpans<
	TColumn extends { key: string; group?: TableColumnGroup },
>(columns: Array<TColumn>): Array<ColumnGroupSpanModel<TColumn>> {
	const spans: Array<ColumnGroupSpanModel<TColumn>> = [];
	for (const column of columns) {
		const last = spans[spans.length - 1];
		if (
			column.group != null &&
			last != null &&
			last.group != null &&
			last.group.id === column.group.id
		) {
			last.columns.push(column);
			continue;
		}
		spans.push({
			key: column.key,
			group: column.group ?? null,
			columns: [column],
		});
	}
	return spans;
}
